import React, { useMemo } from "react";
import { Plus, Trash2, Info, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";

import type { FormikProps } from "formik";
import type { SilaeFormData, SourceType } from "@/types/silae";

import { Input } from "@/components/ui/input";
import { FormikErrorMessage } from "@/components/FormikErrorMessage";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import { SourceItemConfig } from "./SourceItemConfig";

interface ComplexExpressionBuilderProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string; // e.g., "ifBranch.bonuses.0.bases.0.complexExpression"
  title: string;
  depth?: number; // 0 = racine, 1+ = imbriqué
}

/**
 * ComplexExpressionBuilder
 *
 * Ce composant permet de construire des expressions complexes en combinant
 * plusieurs sources de données avec des opérateurs mathématiques.
 *
 * Structure simplifiée :
 * - fieldPrefix: Chemin complet vers le champ complexExpression
 *   ex: "ifBranch.bonuses.0.bases.0.complexExpression"
 * - Utilise getIn(formik.values, fieldPrefix) pour accéder aux valeurs
 * - Chaque item de l'expression est un SilaeSourceFormData complet
 * - Utilise SourceItemConfig pour éviter la duplication de code
 */

export const ComplexExpressionBuilder: React.FC<
  ComplexExpressionBuilderProps
> = ({ formik, fieldPrefix, title, depth = 0 }) => {
  const isNested = depth > 0;

  // Couleur du bouton "Ajouter" alignée avec la palette de cartes par niveau
  const addButtonStyles = [
    "border-violet-600 text-violet-700 hover:bg-violet-100 hover:border-violet-400 dark:border-violet-800 dark:text-violet-400 dark:hover:bg-violet-950/40",
    "border-[#4b6f8c] text-[#4b6f8c] hover:bg-indigo-100 hover:border-indigo-400 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-950/40",
    "border-sky-600 text-sky-700 hover:bg-sky-100 hover:border-sky-400 dark:border-sky-800 dark:text-sky-400 dark:hover:bg-sky-950/40",
  ];
  const addButtonClass =
    addButtonStyles[Math.min(depth, addButtonStyles.length - 1)];

  const { t } = useTranslation();
  const { findVariable } = useSilaeVariables();

  const MATH_OPERATORS = [
    {
      value: "+",
      label: t("complexExpressionBuilder.mathOperator.operators.add"),
    },
    {
      value: "-",
      label: t("complexExpressionBuilder.mathOperator.operators.subtract"),
    },
    {
      value: "*",
      label: t("complexExpressionBuilder.mathOperator.operators.multiply"),
    },
    {
      value: "/",
      label: t("complexExpressionBuilder.mathOperator.operators.divide"),
    },
  ];

  // Récupérer l'expression complexe actuelle (tableau de SilaeSourceFormData)
  const complexExpression = useMemo(() => {
    const value = getIn(formik.values, fieldPrefix);
    return Array.isArray(value) ? value : [];
  }, [formik.values, fieldPrefix]);

  // Récupérer les erreurs de validation pour complexExpression
  const complexExpressionErrors = useMemo(() => {
    const errors = getIn(formik.errors, fieldPrefix);
    return errors;
  }, [formik.errors, fieldPrefix]);


  const generateId = () =>
    `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addExpressionItem = () => {
    const newId = generateId();
    // Créer un objet SilaeSourceFormData complet avec toutes les propriétés par défaut
    const newItem = {
      id: newId,
      hasConditions: false,
      conditions: [],
      conditionsOperators: [],
      sourceType: "fixed" as SourceType,
      amount: 0,
      evpTitle: "",
      evpDefaultValue: "",
      salaryPercentage: "100",
      storedVariableName: "",
      advancedVariable: "",
      advancedVariablePercentage: "100",
      advancedVariableEnableAdditional: false,
      advancedVariableAdditionalOperator: "+",
      advancedVariableAdditionalValue: "0",
      advancedVariableParameters: [],
      advancedVariablePostCallVarName: "",
      advancedVariablePostCallExpression: "",
      complexExpression: [],
      questionnaireVariableType: "double",
      questionnaireVariableIndex: "1",
      questionnaireVariableLabel: "",
      questionnaireVariablePercentage: "100",
      questionnaireVariableValue: "",
      questionnaireVariableOperator: "=",
      manualFormula: "",
      cumulPeriod: "year_current",
      cumulType: "1",
      cumulCustomStartDate: "",
      cumulCustomEndDate: "",
      cumulDivisor: "1",
      cumulStockVarName: "",
      cumulStockVarPeriod: "year_current",
      cumulStockVarDateDebut: "Date(1,1,bul.annee)",
      cumulStockVarDateFin: "Date(31,12,bul.annee)",
      cumulStockVarPercentage: "100",
      cumulStockVarEnableAdditional: false,
      cumulStockVarAdditionalOperator: "+",
      cumulStockVarAdditionalValue: "0",
      variableName: "", // Nom de la variable intermédiaire (spécifique à complex_expression)
      mathOperator: undefined as string | undefined, // Opérateur mathématique entre items
    };

    const updatedExpression = [...complexExpression, newItem];
    formik.setFieldValue(fieldPrefix, updatedExpression);
  };

  const removeExpressionItem = (index: number) => {
    const updatedExpression = [...complexExpression];
    updatedExpression.splice(index, 1);

    // Si on supprime un item qui n'est pas le dernier, s'assurer que l'avant-dernier n'a pas d'opérateur
    if (updatedExpression.length > 0) {
      const lastItem = updatedExpression[updatedExpression.length - 1];
      if (lastItem.mathOperator) {
        lastItem.mathOperator = undefined;
      }
    }

    formik.setFieldValue(fieldPrefix, updatedExpression);
  };

  const renderExpressionItem = (
    item: (typeof complexExpression)[0],
    index: number,
  ) => {
    const isLast = index === complexExpression.length - 1;
    const sourceFieldPrefix = `${fieldPrefix}[${index}]`;
    const sourceType = getIn(formik.values, `${sourceFieldPrefix}.sourceType`);

    // Récupérer les erreurs pour cet élément
    const itemErrors = getIn(formik.errors, sourceFieldPrefix);
    const hasItemErrors =
      itemErrors && itemErrors.length > 0 && typeof itemErrors === "object";

    return (
      <div key={item.id} className="space-y-4">
        <Card className={hasItemErrors ? "border-red-500" : ""}>
          <CardContent className="pt-4">
            {/* En-tête avec numéro et actions */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {t("complexExpressionBuilder.itemNumber", {
                    number: index + 1,
                  })}
                </Badge>
                {sourceType && (
                  <Label className="text-sm font-medium text-muted-foreground">
                    {t(`baseTypeSelect.options.${sourceType}`)}
                  </Label>
                )}
              </div>
              {complexExpression.length !== 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeExpressionItem(index)}
                  disabled={complexExpression.length === 1}
                  className="h-8 w-8 p-0 border-red-100 dark:border-red-900"
                >
                  <Trash2 className="h-4 w-4 text-red-400 dark:text-red-600" />
                </Button>
              )}
            </div>

            {/* Nom de la variable intermédiaire */}
            <div className="space-y-2 mb-4 p-3 bg-muted/50 rounded-lg border border-dashed">
              <Label className="text-sm font-medium">
                {t("complexExpressionBuilder.intermediateVariable.label")}
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                value={item.variableName || ""}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  formik.setFieldValue(
                    `${sourceFieldPrefix}.variableName`,
                    value,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    `${sourceFieldPrefix}.variableName`,
                    true,
                  );
                }}
                placeholder={t(
                  "complexExpressionBuilder.intermediateVariable.placeholder",
                )}
                className={`h-9 ${
                  getIn(formik.errors, `${sourceFieldPrefix}.variableName`)
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : ""
                }`}
                name={`${sourceFieldPrefix}.variableName`}
              />
              <FormikErrorMessage
                formik={
                  formik as unknown as FormikProps<Record<string, unknown>>
                }
                name={`${sourceFieldPrefix}.variableName`}
              />
              <p className="text-xs text-muted-foreground">
                {t("complexExpressionBuilder.intermediateVariable.helpText")}
              </p>
            </div>

            {/* Configuration de la source - Utiliser SourceItemConfig */}
            <SourceItemConfig
              formik={formik}
              sourceFieldPrefix={sourceFieldPrefix}
              complexDepth={depth + 1}
            />

            {/* Affichage des erreurs de validation pour cet élément */}
            {hasItemErrors && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    {typeof itemErrors === "string" ? (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {itemErrors}
                      </p>
                    ) : (
                      Object.entries(itemErrors as Record<string, unknown>).map(
                        ([key, error]) => {
                          if (!error) return null;
                          const errorMessage =
                            typeof error === "string" ? error : String(error);
                          return (
                            <p
                              key={key}
                              className="text-sm text-red-600 dark:text-red-400"
                            >
                              <span className="font-medium">{key}:</span>{" "}
                              {errorMessage}
                            </p>
                          );
                        },
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Opérateur mathématique (sauf pour le dernier item) */}
        {!isLast && (
          <div className="flex justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
              <Label className="text-sm font-medium">
                {t("complexExpressionBuilder.mathOperator.label")}
              </Label>
              <Select
                value={item.mathOperator || "+"}
                onValueChange={(value) =>
                  formik.setFieldValue(
                    `${sourceFieldPrefix}.mathOperator`,
                    value,
                  )
                }
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATH_OPERATORS.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Bandeau info : uniquement au niveau racine pour éviter la répétition */}
      {!isNested && (
        <Card className="border-sky-200 bg-sky-50 p-2 dark:border-sky-800 dark:bg-sky-950/20">
          <CardContent className="px-2">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
              <div className="text-sky-800 dark:text-sky-200 text-xs">
                {t("complexExpressionBuilder.info")}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* En-tête : titre compact + badge de niveau en imbriqué */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isNested && (
            <Badge
              variant="outline"
              className={`text-xs font-mono  ${addButtonClass}`}
            >
              {t("complexExpressionBuilder.nestedLevel", { level: depth })}
            </Badge>
          )}
          <Label
            className={isNested ? "text-sm font-medium" : "text-lg font-medium"}
          >
            {title}
          </Label>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addExpressionItem}
          className={`flex items-center gap-2 ${addButtonClass}`}
        >
          <Plus className="h-4 w-4" />
          {t("complexExpressionBuilder.addElement")}
        </Button>
      </div>

      {/* Erreurs générales */}
      {complexExpressionErrors && (
        <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              {typeof complexExpressionErrors === "string" ? (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {complexExpressionErrors}
                </p>
              ) : Array.isArray(complexExpressionErrors) ? (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {t("complexExpressionBuilder.errors.arrayError")}
                </p>
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {String(complexExpressionErrors)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Liste des items */}
      <div className="space-y-4">
        {complexExpression.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="pt-6 pb-6 text-center text-muted-foreground">
              {t("complexExpressionBuilder.noElements")}
            </CardContent>
          </Card>
        ) : (
          complexExpression.map((item, index) =>
            renderExpressionItem(item, index),
          )
        )}
      </div>

    </div>
  );
};
